import { Fragment } from 'react';
import { Box, FormField, FormHelp, Heading, Input, Label, Paragraph, Text } from '@zillow/constellation-10';
import { Controller, useFormContext } from 'react-hook-form';
import { compact, get, some } from 'lodash-es';
import { ApplicationConfigurationCustomQuestion, IncomeType } from '@zg-rentals/ts-capps-client';
import type { Answers, CurrentIncome } from '../../../../models/rental-application';
import { useIsCustomQuestionEnabled } from '../../../../hooks/useIsCustomQuestionEnabled';
import { validateEmail } from '../../../../utils/validation';
import { getPhoneNumberScreenFormat, isValidUSPhoneNumber } from '../../../../utils/formatters';
import { STEP_IDS } from '../../../../constants/wizard';
import { MaskedPhoneInput } from '../../../Inputs';
import { ContactPermissionCard, useListingOwnerDisplayName } from './ContactPermissionCard';
import { EditInStepLink } from './EditInStepLink';
import type { SupplementalSectionProps } from './supplementalSections';

type EmploymentIncome = {
  index: number;
  income: CurrentIncome;
};

function getEmploymentIncomes(currentIncomes: Answers['currentIncomes']): Array<EmploymentIncome> {
  return (currentIncomes ?? []).flatMap((income, index) =>
    income?.type === IncomeType.EMPLOYMENT ? [{ index, income }] : [],
  );
}

export function hasEmploymentIncome(answers: Answers) {
  return some(answers.currentIncomes, (income) => income?.type === IncomeType.EMPLOYMENT);
}

export function EmployerContactSection({ applicationId, onPersist }: SupplementalSectionProps) {
  const { watch } = useFormContext();
  const isInfoEnabled = useIsCustomQuestionEnabled(ApplicationConfigurationCustomQuestion.EMPLOYER_CONTACT_INFO);
  const isPermissionEnabled = useIsCustomQuestionEnabled(
    ApplicationConfigurationCustomQuestion.EMPLOYER_CONTACT_PERMISSION,
  );
  const listingOwnerName = useListingOwnerDisplayName();
  const employmentIncomes = getEmploymentIncomes(watch('currentIncomes'));

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
      <Box>
        <Heading level={4} css={{ textStyle: 'body-bold', color: 'text.subtle' }}>
          Current employment
        </Heading>
        <Paragraph>
          {employmentIncomes.map(({ index, income }) => (
            <Fragment key={index}>
              Your current employer is <strong>{income.employer?.organization || income.title || 'not set'}</strong>
              .{' '}
            </Fragment>
          ))}
          <EditInStepLink
            applicationId={applicationId}
            stepId={STEP_IDS.CURRENT_INCOME}
            label="Income source"
            onPersist={onPersist}
          />
        </Paragraph>
      </Box>
      {isInfoEnabled &&
        employmentIncomes.map(({ index, income }) => (
          <EmployerContactInfoFields
            key={index}
            index={index}
            organization={employmentIncomes.length > 1 ? income.employer?.organization : null}
          />
        ))}
      {isPermissionEnabled && (
        <ContactPermissionCard
          name="canContactEmployer"
          title={`Allow ${listingOwnerName} to contact your employer`}
          description="Your employer may be contacted to confirm your employment status and income."
        />
      )}
    </Box>
  );
}

function EmployerContactInfoFields({ index, organization }: { index: number; organization?: string | null }) {
  const {
    formState: { errors },
    register,
  } = useFormContext();
  const errorEmail = get(errors, `currentIncomes[${index}].employer.email`);
  const errorPhone = get(errors, `currentIncomes[${index}].employer.phone`);
  const phoneLabelId = `supplemental-currentIncomes${index}-employer-phone-label`;

  return (
    <Box>
      {organization && (
        <Heading level={4} css={{ textStyle: 'body-bold', marginBottom: 'default' }}>
          {organization}
        </Heading>
      )}
      <FormField
        label={<Label id={phoneLabelId}>Employer phone</Label>}
        control={
          <Controller
            name={`currentIncomes[${index}].employer.phone`}
            rules={{
              validate: (phone) => !phone || isValidUSPhoneNumber(phone) || 'Enter a valid phone number',
            }}
            render={({ field }) => <MaskedPhoneInput {...field} aria-labelledby={phoneLabelId} />}
          />
        }
        description={errorPhone?.message ? <FormHelp>{String(errorPhone.message)}</FormHelp> : undefined}
        error={!!errorPhone}
        optional
      />
      <FormField
        label={<Label>Employer email</Label>}
        control={
          <Input
            maxLength={100}
            {...register(`currentIncomes[${index}].employer.email`, { validate: validateEmail })}
            type="email"
          />
        }
        description={errorEmail?.message ? <FormHelp>{String(errorEmail.message)}</FormHelp> : undefined}
        error={!!errorEmail}
        optional
      />
    </Box>
  );
}

export function EmployerContactOverview({ answers }: SupplementalSectionProps) {
  const isInfoEnabled = useIsCustomQuestionEnabled(ApplicationConfigurationCustomQuestion.EMPLOYER_CONTACT_INFO);
  const isPermissionEnabled = useIsCustomQuestionEnabled(
    ApplicationConfigurationCustomQuestion.EMPLOYER_CONTACT_PERMISSION,
  );
  const employmentIncomes = getEmploymentIncomes(answers.currentIncomes);

  return (
    <>
      <Heading level={6} css={{ marginTop: 'tighter', marginBottom: 'tighter' }}>
        Current employment
      </Heading>
      {employmentIncomes.map(({ index, income }) => (
        <Paragraph key={index}>
          {compact([
            income.employer?.organization,
            isInfoEnabled ? income.employer?.email : null,
            isInfoEnabled ? getPhoneNumberScreenFormat(income.employer?.phone) : null,
          ]).join(' · ')}
        </Paragraph>
      ))}
      {isPermissionEnabled && answers.canContactEmployer != null && (
        <>
          <Heading level={6} css={{ marginTop: 'tighter', marginBottom: 'tighter' }}>
            Contact employer?
          </Heading>
          <Text>{answers.canContactEmployer ? 'Yes' : 'No'}</Text>
        </>
      )}
    </>
  );
}
